import { z } from 'zod';

/**
 * Zod schemas for integrator-overridable per-game config surfaces. Stored as JSON
 * in `TenantGame.configOverride`. Each surface is independently nullable —
 * absent surfaces fall back to the game module's `defaultConfig`.
 */

export const timingSchema = z
  .object({
    bettingDurationMs: z.number().int().min(1_000).max(600_000),
    bettingClosingMs: z.number().int().min(0).max(60_000),
    playingMs: z.number().int().min(0).max(60_000),
    revealMs: z.number().int().min(0).max(60_000),
    showingResultMs: z.number().int().min(0).max(60_000),
    nextRoundDelayMs: z.number().int().min(0).max(60_000),
    // Close-boundary grace. Accept bets landing this many ms after
    // bettingClosesAt (skew/latency absorber). Bounded ≤ bettingClosingMs at
    // enforcement so a graced bet can never reach the RNG draw. Default 600ms.
    bettingCloseGraceMs: z.number().int().min(0).max(10_000).optional(),
  })
  .strict();
export type TimingOverride = z.infer<typeof timingSchema>;

/**
 * Two-tier RNG weight schema (greedyking shape). Each round draws once from
 * `primary` (food cabin or house) and once from `special` (pizza/salad/none).
 * Both distributions must sum to 1.0.
 */
export const greedykingWeightsSchema = z
  .object({
    version: z.number().int().min(1),
    primary: z.record(z.string(), z.number().min(0).max(1)),
    special: z.record(z.string(), z.number().min(0).max(1)),
    targetRtp: z.number().min(0).max(2).optional(),
    perSlotHitRateBounds: z
      .record(
        z.string(),
        z.object({ min: z.number().min(0).max(1), max: z.number().min(0).max(1) }),
      )
      .optional(),
  })
  .strict()
  .refine((data) => Math.abs(Object.values(data.primary).reduce((a, b) => a + b, 0) - 1) < 0.01, {
    message: 'primary weights must sum to 1.0 (±0.01)',
    path: ['primary'],
  })
  .refine((data) => Math.abs(Object.values(data.special).reduce((a, b) => a + b, 0) - 1) < 0.01, {
    message: 'special weights must sum to 1.0 (±0.01)',
    path: ['special'],
  });

/**
 * Single-table die weight schema (sicbo shape). Faces 1..6 must sum to 1.0.
 * Greenfield sicbo backend.
 */
export const sicboWeightsSchema = z
  .object({
    version: z.number().int().min(1),
    die: z.record(z.string().regex(/^[1-6]$/), z.number().min(0).max(1)),
  })
  .strict()
  .refine((data) => Math.abs(Object.values(data.die).reduce((a, b) => a + b, 0) - 1) < 0.01, {
    message: 'die weights must sum to 1.0 (±0.01)',
    path: ['die'],
  });

/**
 * Crit-weight schema (dragontiger shape). Rank + suit draws are a uniform fair
 * deck (not operator-tunable), so the only RTP lever is the weighted crit
 * multiplier table — keyed by multiplier string ('1' = no crit), integer
 * relative weights (NOT a probability distribution; `draw` normalises). The
 * dragontiger module reads this off `config.weights.crit` (mergeConfig replaces
 * `config.weights` wholesale), falling back to its shipped `critWeights`.
 * Dragontiger admin config.
 */
export const dragontigerWeightsSchema = z
  .object({
    version: z.number().int().min(1),
    crit: z.record(z.string().regex(/^\d+$/), z.number().int().min(0).max(1_000_000)),
  })
  .strict()
  .refine((data) => Number(data.crit['1'] ?? 0) > 0, {
    message: "crit weight for '1' (no-crit) must be > 0",
    path: ['crit'],
  })
  .refine((data) => Object.values(data.crit).reduce((a, b) => a + b, 0) > 0, {
    message: 'crit weights must sum to a positive total',
    path: ['crit'],
  });

/**
 * Game-agnostic weights override. Each game module knows its own shape;
 * admin UI picks the schema variant by gameId. The orchestrator's
 * `mergeConfig` is shape-blind (replaces wholesale).
 */
export const weightsSchema = z.union([
  greedykingWeightsSchema,
  sicboWeightsSchema,
  dragontigerWeightsSchema,
]);
export type WeightsOverride = z.infer<typeof weightsSchema>;

/**
 * The games that READ a weights override, each with the one shape it reads.
 * Every other game draws from fixed weights (uniform dice, a fair deck, a
 * versioned table) that are not an admin surface. This map is the single
 * source: `GAME_SCOPED_SURFACES.weights` is derived from its keys, and
 * admin-api validates a weights write against its entry, so a game cannot be
 * given a weights surface without a shape, or a shape without the surface.
 */
export const WEIGHTS_SCHEMA_BY_GAME: Record<string, z.ZodType> = {
  greedyking: greedykingWeightsSchema,
  sicbo: sicboWeightsSchema,
  dragontiger: dragontigerWeightsSchema,
};
export type GreedykingWeightsOverride = z.infer<typeof greedykingWeightsSchema>;
export type SicboWeightsOverride = z.infer<typeof sicboWeightsSchema>;
export type DragontigerWeightsOverride = z.infer<typeof dragontigerWeightsSchema>;

/**
 * Two-tier jackpot schema (greedyking shape).
 *
 * `bonusMultiplier` made tunable (was hardcoded 2.0
 * via `JACKPOT_BONUS_MULTIPLIER`); `poolContributionRateByCategory`
 * lets ops dial the veg/meat pool-fill split independently of the
 * scalar `poolContributionRate` — when present, it takes precedence
 * over the scalar. Both new fields are optional for back-compat with
 * existing integrator overrides.
 */
/**
 * Per-category linear pool config. Each veg/meat
 * pool runs independently with its own seed, threshold, mult range,
 * trigger cap, and rake %. All bps fields are integer basis points
 * (1/10_000). When veg + meat are present, the orchestrator prefers
 * them over the legacy tier* / poolContributionRate fields.
 */
const greedykingCategoryPoolSchema = z
  .object({
    seed: z.string().regex(/^\d+$/),
    threshold: z.string().regex(/^\d+$/),
    baseMultBps: z.number().int().min(0).max(1_000_000),
    maxMultBps: z.number().int().min(0).max(1_000_000),
    maxTriggerRateBps: z.number().int().min(0).max(10_000),
    rakeBps: z.number().int().min(0).max(10_000),
  })
  .strict();

export const greedykingJackpotSchema = z
  .object({
    // Deprecated (superseded by per-category `veg`/`meat` below;
    // the orchestrator no longer reads these — see `jackpot.ts`'s
    // `@deprecated` JSDoc on `JackpotConfig`). Made OPTIONAL so a
    // pure per-category save (`veg`/`meat` only, no legacy tier fields) is
    // valid — previously `.strict()` + these being required meant every
    // admin-ui save had to carry dead tier data just to pass validation.
    tier1Threshold: z.string().regex(/^\d+$/).optional(), // bigint as string
    tier1Probability: z.number().min(0).max(1).optional(),
    tier2Threshold: z.string().regex(/^\d+$/).optional(),
    tier2Probability: z.number().min(0).max(1).optional(),
    poolReseed: z.string().regex(/^\d+$/).optional(),
    poolContributionRate: z.number().min(0).max(1).optional(),
    bonusMultiplier: z.number().min(0).max(10).optional(),
    poolContributionRateByCategory: z
      .object({
        veg: z.number().min(0).max(1),
        meat: z.number().min(0).max(1),
      })
      .strict()
      .optional(),
    // Per-category linear pool config. This is the
    // shape the running engine actually reads (`poolIdForBet` +
    // `computeDynamicMult` in round-engine/orchestrator.ts +
    // @apx/game-greedyking's jackpot.ts). Optional for back-compat with
    // integrator overrides that never set it.
    veg: greedykingCategoryPoolSchema.optional(),
    meat: greedykingCategoryPoolSchema.optional(),
  })
  .strict();

/**
 * Trigger-based jackpot schema (sicbo Option A — triple-drain).
 */
export const sicboJackpotSchema = z
  .object({
    minDrainBalanceCredits: z.string().regex(/^\d+$/), // bigint as string
    poolContributionRate: z.number().min(0).max(1),
  })
  .strict();

/**
 * Game-agnostic jackpot override. Orchestrator's `mergeConfig` uses
 * `coerceMaybeBigInt` over all numeric-string fields, so future shapes
 * pass through too.
 */
export const jackpotSchema = z.union([greedykingJackpotSchema, sicboJackpotSchema]);
export type JackpotOverride = z.infer<typeof jackpotSchema>;
export type GreedykingJackpotOverride = z.infer<typeof greedykingJackpotSchema>;
export type SicboJackpotOverride = z.infer<typeof sicboJackpotSchema>;

export const bigWinnerSchema = z
  .object({
    threshold: z.string().regex(/^\d+$/), // bigint as string, in minor units (USD cents) of every currency alike
  })
  .strict();
export type BigWinnerOverride = z.infer<typeof bigWinnerSchema>;

/**
 * Per-food payout multipliers (greedyking shape).
 *
 * Keys = FoodItem enum. Values = integer multiplier applied to a winning
 * `basePayout = betAmount * multiplier`. Defaults live on
 * `DEFAULT_GREEDYKING_CONFIG.multipliers`.
 */
export const greedykingMultipliersSchema = z
  .object({
    carrots: z.number().int().min(1).max(1000),
    pakcoi: z.number().int().min(1).max(1000),
    corn: z.number().int().min(1).max(1000),
    tomato: z.number().int().min(1).max(1000),
    hotdog: z.number().int().min(1).max(1000),
    bbq: z.number().int().min(1).max(1000),
    meat: z.number().int().min(1).max(1000),
    steak: z.number().int().min(1).max(1000),
  })
  .strict();
export type GreedykingMultipliersOverride = z.infer<typeof greedykingMultipliersSchema>;

/**
 * Basis-points value (1/100_000_000). Non-negative integer; the upper bound
 * (100_000_000 bps = 10_000×) comfortably exceeds any real payout return and
 * matches the runtime payout-type contracts (sicbo/dragontiger emit integer
 * bps). Used for sicbo/dragontiger payout-multiplier overrides.
 */
const BPS = z.number().int().min(0).max(100_000_000);

// Mirrors SICBO_BET_TYPES in @apx/game-sicbo (packages/games/sicbo/src/types.ts).
// Kept local to avoid an engine→games package dependency. Keep in sync if bet types change.
const SICBO_BET_TYPE_KEYS = [
  'SICBO_BIG',
  'SICBO_SMALL',
  'SICBO_ODD',
  'SICBO_EVEN',
  'SICBO_TRIPLE_ANY',
  'SICBO_TRIPLE_1',
  'SICBO_TRIPLE_2',
  'SICBO_TRIPLE_3',
  'SICBO_TRIPLE_4',
  'SICBO_TRIPLE_5',
  'SICBO_TRIPLE_6',
  'SICBO_TOTAL_4',
  'SICBO_TOTAL_5',
  'SICBO_TOTAL_6',
  'SICBO_TOTAL_7',
  'SICBO_TOTAL_8',
  'SICBO_TOTAL_9',
  'SICBO_TOTAL_10',
  'SICBO_TOTAL_11',
  'SICBO_TOTAL_12',
  'SICBO_TOTAL_13',
  'SICBO_TOTAL_14',
  'SICBO_TOTAL_15',
  'SICBO_TOTAL_16',
  'SICBO_TOTAL_17',
  'SICBO_SINGLE_1',
  'SICBO_SINGLE_2',
  'SICBO_SINGLE_3',
  'SICBO_SINGLE_4',
  'SICBO_SINGLE_5',
  'SICBO_SINGLE_6',
  'SICBO_DOUBLE_1',
  'SICBO_DOUBLE_2',
  'SICBO_DOUBLE_3',
  'SICBO_DOUBLE_4',
  'SICBO_DOUBLE_5',
  'SICBO_DOUBLE_6',
] as const;

// Mirrors DT_BET_TYPES in @apx/game-dragontiger (packages/games/dragontiger/src/types.ts).
const DT_BET_TYPE_KEYS = [
  'DT_DRAGON',
  'DT_TIGER',
  'DT_TIE',
  'DT_DRAGON_BLACK',
  'DT_DRAGON_RED',
  'DT_TIGER_BLACK',
  'DT_TIGER_RED',
  'DT_SUM_2_26',
  'DT_SUM_3_6',
  'DT_SUM_7_11',
  'DT_SUM_12_16',
  'DT_SUM_17_21',
  'DT_SUM_22_25',
] as const;

// Mirrors BAC_BET_TYPES in @apx/game-baccarat (packages/games/baccarat/src/types.ts).
const BAC_BET_TYPE_KEYS = [
  'BAC_PLAYER',
  'BAC_BANKER',
  'BAC_TIE',
  'BAC_PLAYER_PAIR',
  'BAC_BANKER_PAIR',
] as const;

// Mirrors SGL_BET_TYPES in @apx/game-singaporelottery (packages/games/singaporelottery/src/types.ts).
// Kept local to avoid an engine→games package dependency. Keep in sync if bet types change.
const SGL_BET_TYPE_KEYS = [
  'SGL_SUM_BIG',
  'SGL_SUM_SMALL',
  'SGL_SUM_ODD',
  'SGL_SUM_EVEN',
  'SGL_FIRST_BIG',
  'SGL_FIRST_SMALL',
  'SGL_FIRST_ODD',
  'SGL_FIRST_EVEN',
  'SGL_FIRST_0',
  'SGL_FIRST_1',
  'SGL_FIRST_2',
  'SGL_FIRST_3',
  'SGL_FIRST_4',
  'SGL_FIRST_5',
  'SGL_FIRST_6',
  'SGL_FIRST_7',
  'SGL_FIRST_8',
  'SGL_FIRST_9',
  'SGL_LAST2_BIG',
  'SGL_LAST2_SMALL',
  'SGL_LAST2_ODD',
  'SGL_LAST2_EVEN',
] as const;

// Mirrors SDR_BET_TYPES in @apx/game-shortdicerush and LDR_BET_TYPES in
// @apx/game-longdicerush. Kept local to avoid an engine→games package
// dependency, like every other list here; the drift guard is
// `rtp-solver.dicerush.test.ts`, which derives the real lists from each game
// package and compares.
const SDR_BET_TYPE_KEYS = [
  'SDR_SMALL',
  'SDR_BIG',
  'SDR_ODD',
  'SDR_EVEN',
  'SDR_ANY_1',
  'SDR_ANY_2',
  'SDR_ANY_3',
  'SDR_ANY_4',
  'SDR_ANY_5',
  'SDR_ANY_6',
  'SDR_DOUBLE_1',
  'SDR_DOUBLE_2',
  'SDR_DOUBLE_3',
  'SDR_DOUBLE_4',
  'SDR_DOUBLE_5',
  'SDR_DOUBLE_6',
] as const;

const LDR_BET_TYPE_KEYS = [
  'LDR_SMALL',
  'LDR_BIG',
  'LDR_ODD',
  'LDR_EVEN',
  'LDR_ANY_1',
  'LDR_ANY_2',
  'LDR_ANY_3',
  'LDR_ANY_4',
  'LDR_ANY_5',
  'LDR_ANY_6',
  'LDR_DOUBLE_1',
  'LDR_DOUBLE_2',
  'LDR_DOUBLE_3',
  'LDR_DOUBLE_4',
  'LDR_DOUBLE_5',
  'LDR_DOUBLE_6',
] as const;

// Mirrors WG_BET_TYPES in @apx/game-wingo, in board order: the three colours,
// then the numbers 0-9. Kept local like every other list here, and for a
// reason a typecheck does not show: this file is bundled into the admin
// console, and a game package's entry point reaches the seeded draw, which
// needs `node:crypto` and fails the browser build outright. The drift guard is
// `schemas.wingo.test.ts`, which derives the real list from the package and
// compares, and which also holds this file to importing no game package.
const WINGO_BET_TYPE_KEYS = [
  'WG_GREEN',
  'WG_PURPLE',
  'WG_RED',
  'WG_NUM_0',
  'WG_NUM_1',
  'WG_NUM_2',
  'WG_NUM_3',
  'WG_NUM_4',
  'WG_NUM_5',
  'WG_NUM_6',
  'WG_NUM_7',
  'WG_NUM_8',
  'WG_NUM_9',
] as const;

/**
 * Wingo's stake grid, in credits: the smallest stake on which
 * `floor(stake × bps / 10000)` is EXACT for every rate in its shipped paytable.
 *
 * The package DERIVES this from the paytable (`STAKE_GRID_CREDITS`); it is
 * mirrored here only because this file cannot import the package (see the note
 * on WINGO_BET_TYPE_KEYS). It is typed once: the table schema,
 * `STAKE_GRANULARITY_BY_GAME` and the multiplier step below all read this
 * constant, and `schemas.wingo.test.ts` pins it to the package's, so a paytable
 * change that moves the grid fails that test rather than leaving this behind.
 *
 * BEFORE CHANGING IT: the multiplier step follows from it, and the multipliers
 * schema is also what a STORED override is read through. An override saved on
 * the old step would stop parsing, and a tenant config that fails to parse is
 * read as `{}` — its kill switch, maintenance flag and table limits go with
 * it. Move stored overrides onto the new step first.
 */
const WINGO_STAKE_GRID = 50;

/**
 * A Wingo multiplier must keep the grid at `WINGO_STAKE_GRID` or finer. Any bps
 * that is a multiple of this step does: `10000 / gcd(bps, 10000)` then divides
 * the grid. Same formula as the package's `RETURN_BPS_STEP`, pinned to it by
 * the same test.
 */
const WINGO_RETURN_BPS_STEP = 10_000 / WINGO_STAKE_GRID;

// Mirrors ANP_BET_TYPES in @apx/game-animalparty: the nine single animals and
// the two groups. There is no bet on the King. Kept local for the same reason
// as every list above — this file is bundled into the admin-ui, and a game
// package reaches `node:crypto` — and guarded by
// `rtp-solver.animalparty.test.ts`, which compares it with the package.
const ANP_BET_TYPE_KEYS = [
  'ANP_PANDA',
  'ANP_RABBIT',
  'ANP_MONKEY',
  'ANP_TIGER',
  'ANP_ZEBRA',
  'ANP_ELEPHANT',
  'ANP_GORILLA',
  'ANP_DRAGON',
  'ANP_UNICORN',
  'ANP_LAND',
  'ANP_MAGIC',
] as const;

/** Total-return bps that make 1.00x: `bps / BPS_SCALE` is the multiplier. */
const BPS_SCALE = 10_000;

/**
 * The step an Animal Party multiplier moves in. Mirrors `RETURN_BPS_STEP` in
 * @apx/game-animalparty, pinned by `rtp-solver.animalparty.test.ts`.
 *
 * It is the ONE number this file holds for that game's stake grid: both the
 * multipliers schema and the table schema below are derived from it, so the
 * two cannot be edited apart.
 */
const ANP_RETURN_BPS_STEP = 1_000;

/**
 * Fish Prawn Crab's stake grid, in credits: the smallest stake on which
 * `floor(stake × bps / 10000)` is EXACT for every rate its shipped paytable can
 * settle at (the three count-scaled Three Soldiers returns, the Pair and the
 * Long Card).
 *
 * The package DERIVES this from the paytable (`STAKE_GRID_CREDITS`). It is
 * mirrored here, not imported, because this file is bundled into the admin
 * console and a game package's entry point reaches the seeded draw, which needs
 * `node:crypto` and fails the browser build outright. It is typed once: the
 * table schema, `STAKE_GRANULARITY_BY_GAME` and the multiplier step below all
 * read this constant, and `schemas.fishprawncrab.test.ts` pins it to the
 * package's, so a paytable change that moves the grid fails that test rather
 * than leaving this behind.
 *
 * BEFORE CHANGING IT: the multiplier step follows from it, and the multipliers
 * schema is also what a STORED override is read through. An override saved on
 * the old step would stop parsing, and a tenant config that fails to parse is
 * read as `{}` — its kill switch, maintenance flag and table limits go with
 * it. Move stored overrides onto the new step first.
 */
const FISHPRAWNCRAB_STAKE_GRID = 100;

/**
 * A Fish Prawn Crab figure must keep the grid at `FISHPRAWNCRAB_STAKE_GRID` or
 * finer. Any bps that is a multiple of this step does — and so does every
 * count-scaled Three Soldiers return built from such a per-die rate
 * (`10000 + n × rate`). Same formula as the package's `RETURN_BPS_STEP`, pinned
 * to it by the same test. 100 bps is 0.01×: figures move in whole hundredths.
 */
const FISHPRAWNCRAB_RETURN_BPS_STEP = 10_000 / FISHPRAWNCRAB_STAKE_GRID;

/**
 * Sicbo payout-multiplier override.
 *
 * Mirrors the runtime `SicboMultipliers { returns: Record<SicboBetType, number>;
 * singleWinningsPerDieBps: number }`. `returns` is a PARTIAL record (string-keyed
 * by SicboBetType) — operators may override only some bet types; unspecified
 * ones fall back to baked defaults at merge/payout time. All values are integer
 * basis points. Dynamic RTP.
 */
export const sicboMultipliersSchema = z
  .object({
    version: z.number().int().min(1),
    returns: z.record(z.enum(SICBO_BET_TYPE_KEYS), BPS),
    singleWinningsPerDieBps: BPS,
  })
  .strict();
export type SicboMultipliersOverride = z.infer<typeof sicboMultipliersSchema>;

/**
 * Dragon&Tiger payout-multiplier override.
 *
 * Mirrors the runtime `DragonTigerMultipliers { returns: Record<DragonTigerBetType,
 * number>; tieRefundBps: number; maxReturnBps: number }`. `returns` is a PARTIAL
 * record (string-keyed by DragonTigerBetType); unspecified bet types fall back to
 * baked defaults. All values are integer basis points. Dynamic RTP.
 */
export const dragontigerMultipliersSchema = z
  .object({
    version: z.number().int().min(1),
    returns: z.record(z.enum(DT_BET_TYPE_KEYS), BPS),
    tieRefundBps: BPS,
    maxReturnBps: BPS,
  })
  .strict();
export type DragontigerMultipliersOverride = z.infer<typeof dragontigerMultipliersSchema>;

/**
 * Singapore Lottery payout-multiplier override.
 *
 * Mirrors the runtime `SingaporeLotteryMultipliers { version?: number;
 * returns: Record<SingaporeLotteryBetType, number> }` (packages/games/
 * singaporelottery/src/types.ts). A FLAT paytable: `returns` is a PARTIAL record
 * string-keyed by SingaporeLotteryBetType — operators may override only some
 * cells; unspecified ones fall back to the baked defaults at payout time
 * (19000 bps = 1.90× for every Big/Small/Odd/Even cell, 95000 bps = 9.50× for
 * each exact first digit — both a 5.0% house edge). All values are integer
 * total-return basis points.
 * No RNG-weight lever exists — the digit draw is a fixed uniform 1/10 per
 * position — so this is singaporelottery's only tunable RTP surface.
 */
export const singaporeLotteryMultipliersSchema = z
  .object({
    version: z.number().int().min(1),
    returns: z.record(z.enum(SGL_BET_TYPE_KEYS), BPS),
  })
  .strict();
export type SingaporeLotteryMultipliersOverride = z.infer<typeof singaporeLotteryMultipliersSchema>;

/**
 * Baccarat payout-multiplier override.
 *
 * Mirrors the runtime `BaccaratMultipliers { version?: number; returns:
 * Record<BaccaratBetType, number>; tiePushBps: number }` (packages/games/
 * baccarat/src/types.ts). `returns` is a PARTIAL record — operators may
 * override only some cells; the rest fall back to the baked defaults
 * (20000 = 2.00x Player, 19500 = 1.95x Banker with the 5% commission baked in,
 * 90000 = 9.00x Tie, 120000 = 12.00x each pair).
 *
 * `tiePushBps` is what a Player/Banker bet returns when the coup ties —
 * 10000 (stake back, the standard push) by default. It is a distinct surface
 * from `returns` because a tie is neither a win nor a loss.
 *
 * No RNG-weight lever exists: the card draw is uniform and the drawing rules
 * are fixed, so these multipliers are baccarat's ONLY tunable RTP surface.
 * Every cell's RTP is affine in its multiplier, which is what lets the admin
 * RTP tab preview an exact recomputed figure with no simulation.
 *
 * Discriminates from dragontiger (which carries `tieRefundBps`/`maxReturnBps`)
 * by requiring `tiePushBps`, and every schema here is `.strict()`.
 */
export const baccaratMultipliersSchema = z
  .object({
    version: z.number().int().min(1),
    returns: z.record(z.enum(BAC_BET_TYPE_KEYS), BPS),
    tiePushBps: BPS,
  })
  .strict();
export type BaccaratMultipliersOverride = z.infer<typeof baccaratMultipliersSchema>;

/**
 * Crash RTP override.
 *
 * Mirrors the runtime `CrashMultipliers { version?: number; rtpBps: number }`
 * (packages/games/crash/src/types.ts). Crash has no paytable — the player names
 * the multiplier — so its one tunable is the RTP constant `k` itself:
 * `P(reach x) = k / x` and a win pays `x`, so the expected return is exactly `k`
 * at every target. It lives on `multipliers` because that is a surface
 * `mergeGameConfig` carries through to the draw.
 *
 * Bounds are total-return bps (1/10_000, NOT the 1/100_000_000 `BPS` above):
 *   - ≥ 5000 catches a unit slip (typing 96 for 96%) that would otherwise pay
 *     almost nothing and still verify cleanly.
 *   - ≤ 10000 is a hard ceiling, not a soft cap a super admin can override: the
 *     surface is flat, so k > 100% loses on EVERY target at once — there is no
 *     mix of bets for a promo to balance against.
 *
 * Without this branch a crash RTP save failed every other union member (all
 * `.strict()`, none has `rtpBps`), and a stored crash override carrying it
 * failed `tenantGameConfigSchema` wholesale — which `IntegratorConfigService`
 * reads as `{}`, silently dropping the tenant's kill switch, maintenance and
 * table limits along with it.
 */
export const crashMultipliersSchema = z
  .object({
    version: z.number().int().min(1).optional(),
    rtpBps: z.number().int().min(5_000).max(10_000),
  })
  .strict();
export type CrashMultipliersOverride = z.infer<typeof crashMultipliersSchema>;

/**
 * Mines Chest settings override.
 *
 * Mirrors the runtime `MinesChestMultipliers { version?, rtpBps, maxPicks }`
 * (packages/games/mineschest/src/types.ts). Two tunables, and either may be
 * overridden on its own: `mergeGameConfig` merges this surface field by field
 * over the game's defaults.
 *
 *   - `rtpBps` is the RTP constant `k`, in total-return bps (1/10_000). A
 *     winning ticket of `n` chests returns `k` over its chance of winning, cut
 *     to two decimals. 5000 – 10000, in steps of 50: one chest pays
 *     `k × 2 / 100` hundredths, a whole number only when 50 divides `k`, so a
 *     stray 9599 would pay 1.91x and return 95.50%. At least 5000 catches a
 *     unit slip (96 typed for 96%); at most 10000 because above it every
 *     ticket loses money for the house at once.
 *   - `maxPicks` is the most chests one ticket may hold, 1 – 10. It is a
 *     betting rule, not a payout, and sits here because this is the surface
 *     the engine merges per tenant AND serves to the board.
 *
 * This is the game's OWN schema, bound to its id in the per-game maps. It is
 * also a member of the game-agnostic union below, which is what parses a
 * stored override on read: without a member that accepts `maxPicks`, a stored
 * override fails `tenantGameConfigSchema` wholesale and the tenant's kill
 * switch, maintenance and table limits are dropped along with it.
 */
export const mineschestMultipliersSchema = z
  .object({
    version: z.number().int().min(1).optional(),
    rtpBps: z.number().int().min(5_000).max(10_000).multipleOf(50).optional(),
    maxPicks: z.number().int().min(1).max(10).optional(),
  })
  .strict()
  .refine((v) => v.rtpBps !== undefined || v.maxPicks !== undefined, {
    message: 'Set rtpBps, maxPicks, or both',
  });
export type MinesChestMultipliersOverride = z.infer<typeof mineschestMultipliersSchema>;

/**
 * Space Hero RTP override.
 *
 * Mirrors the runtime `SpaceHeroMultipliers { version?: number; rtpBps: number }`
 * (packages/games/spacehero/src/types.ts). The player names the multiplier, so
 * there is no paytable to tune: the one tunable is the RTP constant `k`.
 * `P(reach x) = k / x` and a win returns `x`, so the expected return is exactly
 * `k` at every target. It lives on `multipliers` because that is a surface
 * `mergeGameConfig` carries through to the draw.
 *
 * Bounds are total-return bps (1/10_000, NOT the 1/100_000_000 `BPS` above):
 *   - at least 5000 catches a unit slip (96 typed for 96%), which would pay
 *     almost nothing and still verify cleanly.
 *   - at most 10000 is a hard ceiling: the surface is flat, so a `k` above 100%
 *     loses on every target at once and no mix of bets could balance it.
 *
 * This is the game's OWN schema, bound to its id in the per-game maps. It is
 * also a member of the game-agnostic union below, which is what parses a
 * stored override on read: without a member that accepts `rtpBps`, a stored
 * override fails `tenantGameConfigSchema` wholesale and the tenant's kill
 * switch, maintenance and table limits are dropped along with it.
 */
export const spaceheroMultipliersSchema = z
  .object({
    version: z.number().int().min(1).optional(),
    rtpBps: z.number().int().min(5_000).max(10_000),
  })
  .strict();
export type SpaceHeroMultipliersOverride = z.infer<typeof spaceheroMultipliersSchema>;

/**
 * Game-agnostic multipliers override. Each game module knows its own shape;
 * admin UI picks the schema variant by gameId. The shapes discriminate cleanly:
 * greedyking is a flat food-name → integer map with NO `version`, while sicbo,
 * dragontiger, and singaporelottery all require `version` plus a `returns` record
 * and are distinguished by `singleWinningsPerDieBps` (sicbo),
 * `tieRefundBps`/`maxReturnBps` (dragontiger), or neither (singaporelottery —
 * `returns` only). Every schema is `.strict()`, so cross-shape objects fail every
 * other branch and each game's `returns` keys are enum-guarded — no union
 * ambiguity. Crash is the only shape with `rtpBps` and no `returns`. Wingo is
 * the only one whose `returns` is a full, strict table rather than a partial
 * record, so an empty or partial `returns` can never resolve to it.
 */
/**
 * Dice Rush payout-multiplier override — one schema per game.
 *
 * Mirrors each game's runtime multipliers type: `{ version?: number; returns:
 * Record<BetType, number> }`. `returns` is a PARTIAL record; unspecified bet
 * types fall back to that game's baked defaults at merge/payout time.
 *
 * The two are separate games with separate packages, and their bet-type keys
 * are what tells them apart on the wire — an `SDR_*` payload must never
 * validate onto Long Dice Rush. Neither has an RNG-weight lever: the dice are
 * uniform and fixed, so these multipliers are the only tunable RTP surface, and
 * every area's RTP is affine in its own multiplier.
 *
 * **Every multiplier must keep the chip ladder on the 25-credit stake grid.**
 * Settlement floors `stake × bps / 10000`, so a rate whose gcd with 10000 drops
 * below 400 needs a finer ladder or it silently underpays every win. The table
 * schema below enforces the grid on the chips; a custom multiplier is the
 * operator's side of that bargain.
 */
export const shortDiceRushMultipliersSchema = z
  .object({
    version: z.number().int().min(1),
    returns: z.record(z.enum(SDR_BET_TYPE_KEYS), BPS),
  })
  .strict();
export type ShortDiceRushMultipliersOverride = z.infer<typeof shortDiceRushMultipliersSchema>;

export const longDiceRushMultipliersSchema = z
  .object({
    version: z.number().int().min(1),
    returns: z.record(z.enum(LDR_BET_TYPE_KEYS), BPS),
  })
  .strict();
export type LongDiceRushMultipliersOverride = z.infer<typeof longDiceRushMultipliersSchema>;

/**
 * One Wingo rate: a bps inside the standard bound AND on the game's own step.
 *
 * The table schema below holds every chip and minimum to the stake grid the
 * SHIPPED paytable needs. A rate that needs a coarser grid would underpay every
 * win on a ladder this file has already accepted, and nothing would error: a
 * solved 19 612 bps on a colour needs a 2 500-credit grid. Any multiple of the
 * step keeps the grid where it is or finer, so that is the only kind of rate
 * accepted here.
 */
const WINGO_BPS = BPS.multipleOf(WINGO_RETURN_BPS_STEP, {
  message:
    `Wingo multipliers must be a multiple of ${WINGO_RETURN_BPS_STEP} bps — any other rate ` +
    `takes the chip ladder off the ${WINGO_STAKE_GRID}-credit stake grid, where every win is ` +
    `silently rounded down`,
});

/**
 * Wingo payout-multiplier override.
 *
 * Mirrors the runtime `WingoMultipliers { version?: number; returns:
 * Record<WingoBetType, number> }`.
 *
 * `returns` is the WHOLE table, not a partial record: all thirteen bet types
 * must be present and no other key is accepted. A stored override therefore
 * always says what every spot pays, and a payload built for another game
 * (other keys) or a truncated one (missing keys) is refused rather than
 * silently filled in from defaults.
 *
 * No RNG-weight lever exists: the draw is uniform over 0-9 and fixed, so these
 * multipliers are the game's ONLY tunable RTP surface, and every spot's RTP is
 * affine in its own multiplier.
 */
export const wingoMultipliersSchema = z
  .object({
    version: z.number().int().min(1),
    returns: z
      .object(
        Object.fromEntries(WINGO_BET_TYPE_KEYS.map((betType) => [betType, WINGO_BPS])) as Record<
          (typeof WINGO_BET_TYPE_KEYS)[number],
          typeof WINGO_BPS
        >,
      )
      .strict(),
  })
  .strict();
export type WingoMultipliersOverride = z.infer<typeof wingoMultipliersSchema>;

/**
 * One Fish Prawn Crab figure: a POSITIVE bps on the game's own step.
 *
 * The table schema below holds every chip and minimum to the stake grid the
 * SHIPPED paytable needs. A figure that needs a coarser grid would underpay
 * every win on a ladder this file has already accepted, and nothing would
 * error: a solved 69 120 bps Long Card needs a 125-credit grid. Any multiple of
 * the step keeps the grid where it is or finer, so that is the only kind of
 * figure accepted. Zero is refused too: a family priced at nothing is a typo,
 * not a configuration.
 */
const FISHPRAWNCRAB_BPS = BPS.min(FISHPRAWNCRAB_RETURN_BPS_STEP, {
  message: 'Fish Prawn Crab figures must be positive',
}).multipleOf(FISHPRAWNCRAB_RETURN_BPS_STEP, {
  message:
    `Fish Prawn Crab figures must be a multiple of ${FISHPRAWNCRAB_RETURN_BPS_STEP} bps — any ` +
    `other figure takes the chip ladder off the ${FISHPRAWNCRAB_STAKE_GRID}-credit stake grid, ` +
    `where every win is silently rounded down`,
});

/**
 * Fish Prawn Crab payout-multiplier override.
 *
 * Mirrors the runtime `FpcMultipliers { version?: number;
 * singleWinningsPerDieBps: number; pairReturnBps: number; longCardReturnBps:
 * number }`: ONE figure per bet family, because every symbol is equally likely
 * and a different figure per symbol would have no reason.
 *
 *  - `singleWinningsPerDieBps` — Three Soldiers is count-scaled: a win returns
 *    `10000 + n × this`, where n (1-3) is how many dice show the symbol.
 *  - `pairReturnBps`, `longCardReturnBps` — fixed total returns.
 *
 * All three are required and no other key is accepted, so a stored override
 * always says what every family pays, and a payload built for another game is
 * refused rather than silently filled in from defaults. It has no `returns`
 * record at all, which is what keeps it apart from every other shape in the
 * union below (every one of them is `.strict()`).
 *
 * No RNG-weight lever exists: the dice are uniform and fixed, so these three
 * figures are the game's ONLY tunable RTP surface, and each family's RTP is
 * affine in its own figure.
 */
export const fishprawncrabMultipliersSchema = z
  .object({
    version: z.number().int().min(1),
    singleWinningsPerDieBps: FISHPRAWNCRAB_BPS,
    pairReturnBps: FISHPRAWNCRAB_BPS,
    longCardReturnBps: FISHPRAWNCRAB_BPS,
  })
  .strict();
export type FishPrawnCrabMultipliersOverride = z.infer<typeof fishprawncrabMultipliersSchema>;

/**
 * Animal Party payout-multiplier override.
 *
 * Mirrors the runtime `AnimalPartyMultipliers { version?: number; returns:
 * Record<AnimalPartyBetType, number> }`. `returns` is a PARTIAL record over the
 * eleven spots; an unlisted spot keeps its baked default at payout time.
 *
 * The outcome weights are a versioned constant and NOT an admin surface, so
 * these multipliers are the game's only tunable RTP lever, and each spot's RTP
 * is `P(win) x multiple`: affine in its own multiple.
 *
 * **Every rate is a positive multiple of `ANP_RETURN_BPS_STEP`.** Settlement
 * floors `stake x bps / 10000`, which is exact only on a stake grid of
 * `10000 / gcd(10000, every bps)`. Holding every rate to the step holds that
 * grid at `10000 / step` credits whatever an operator saves, and the table
 * schema below enforces exactly that grid on the chips. A free-form rate such
 * as 21 053 would move the grid to 10 000 credits and silently underpay every
 * chip on the rail. A rate of 0 is refused as well: it turns a spot into one
 * that can never pay while still taking stakes.
 */
export const animalPartyMultipliersSchema = z
  .object({
    version: z.number().int().min(1),
    returns: z.record(
      z.enum(ANP_BET_TYPE_KEYS),
      z
        .number()
        .int()
        .positive()
        .max(100_000_000)
        .refine((bps) => bps % ANP_RETURN_BPS_STEP === 0, {
          message: `must be a multiple of ${ANP_RETURN_BPS_STEP} bps, or wins come off the stake grid and are rounded down`,
        }),
    ),
  })
  .strict();
export type AnimalPartyMultipliersOverride = z.infer<typeof animalPartyMultipliersSchema>;

export const multipliersSchema = z.union([
  greedykingMultipliersSchema,
  sicboMultipliersSchema,
  dragontigerMultipliersSchema,
  singaporeLotteryMultipliersSchema,
  baccaratMultipliersSchema,
  shortDiceRushMultipliersSchema,
  longDiceRushMultipliersSchema,
  wingoMultipliersSchema,
  fishprawncrabMultipliersSchema,
  animalPartyMultipliersSchema,
  crashMultipliersSchema,
  mineschestMultipliersSchema,
  spaceheroMultipliersSchema,
]);
export type MultipliersOverride = z.infer<typeof multipliersSchema>;

/**
 * Per-category match-bonus rates (greedyking shape).
 *
 * Applied as a percentage top-up on a winning primary match:
 *   `matchBonus = basePayout * categoryBonusRates[category]`
 *
 * Defaults: veg 20%, meat 30% — mirroring the constants table that
 * shipped before these became configurable. Tunable so the
 * veg/meat balance can be dialled independently.
 */
export const greedykingCategoryBonusSchema = z
  .object({
    veg: z.number().min(0).max(2),
    meat: z.number().min(0).max(2),
  })
  .strict();
export type GreedykingCategoryBonusOverride = z.infer<typeof greedykingCategoryBonusSchema>;
export const categoryBonusSchema = greedykingCategoryBonusSchema;
export type CategoryBonusOverride = z.infer<typeof categoryBonusSchema>;

/**
 * Streak-bonus surface.
 *
 *   enabled        — turn the system on/off without ripping config.
 *   bonusPerStep   — additive multiplier added per consecutive same-
 *                    category win. E.g. `0.1` ⇒ a 3-long streak boosts
 *                    payout by +30%.
 *   maxStreak      — cap the bonus exponent so a hot run cannot
 *                    accelerate beyond an operator-set ceiling.
 *
 * Game-agnostic on purpose: each game decides what "same category"
 * means via its `getBetCategoryForStreak` hook.
 */
export const streakSchema = z
  .object({
    enabled: z.boolean(),
    bonusPerStep: z.number().min(0).max(1),
    maxStreak: z.number().int().min(1).max(100),
  })
  .strict();
export type StreakOverride = z.infer<typeof streakSchema>;

export const leaderboardSchema = z
  .object({
    windowHours: z.number().int().positive().max(720),
    topN: z.number().int().positive().max(100),
    refreshIntervalMs: z.number().int().min(1_000).max(60_000),
  })
  .strict();
export type LeaderboardOverride = z.infer<typeof leaderboardSchema>;

const rulesLocaleKey = z.string().min(2).max(8);
const perLocaleMarkdown = z.record(rulesLocaleKey, z.string().max(50_000));

/**
 * Player-facing rules markdown, authored PER LOCALE so the Rules tab follows the
 * player's chosen game language. `default` names the fallback locale.
 *
 * Back-compat: the legacy single-locale shape `{ markdown, locale }` is still
 * accepted on read and normalized to `{ byLocale, default }`. This is critical —
 * `IntegratorConfigService.get` drops ALL of an integrator's overrides if the
 * config fails to parse (see service.ts), so a strict-only new shape would
 * silently revert existing integrators to defaults.
 */
export const rulesSchema = z
  .union([
    z.object({ byLocale: perLocaleMarkdown, default: rulesLocaleKey.optional() }).strict(),
    z.object({ markdown: z.string().max(50_000), locale: z.string().default('id') }).strict(),
  ])
  .transform((v) =>
    'byLocale' in v
      ? { byLocale: v.byLocale, default: v.default }
      : { byLocale: { [v.locale]: v.markdown }, default: v.locale },
  );
export type RulesOverride = z.infer<typeof rulesSchema>;

/**
 * Resolve the markdown for a requested locale from a RAW stored rules value
 * (may be the per-locale shape, the legacy `{ markdown, locale }`, or malformed).
 * Tolerant — never throws; returns null when nothing usable is present so the
 * caller can fall back to a per-game default. Fallback chain:
 * requested locale → `default` locale → `en` → any authored locale.
 */
export function resolveRulesMarkdown(rawRules: unknown, locale: string): string | null {
  if (!rawRules || typeof rawRules !== 'object') return null;
  const r = rawRules as Record<string, unknown>;
  let byLocale: Record<string, string> | null = null;
  let def: string | undefined;
  if (r.byLocale && typeof r.byLocale === 'object') {
    byLocale = r.byLocale as Record<string, string>;
    def = typeof r.default === 'string' ? r.default : undefined;
  } else if (typeof r.markdown === 'string') {
    const loc = typeof r.locale === 'string' ? r.locale : 'en';
    byLocale = { [loc]: r.markdown };
    def = loc;
  }
  if (!byLocale) return null;
  const pick =
    byLocale[locale] ??
    (def ? byLocale[def] : undefined) ??
    byLocale.en ??
    Object.values(byLocale)[0];
  return typeof pick === 'string' && pick.trim() ? pick : null;
}

export const schedulerSchema = z
  .object({
    paused: z.boolean(),
    pausedReason: z.string().optional(),
  })
  .strict();
export type SchedulerOverride = z.infer<typeof schedulerSchema>;

/**
 * Wallet anti-abuse — per-integrator-game flag set + caps.
 *
 *   killSwitch:               refuse all new bets + skip auto-schedule when true.
 *   betCapPerRound:           reject the (N+1)th bet on the same round from the same player. Default 50.
 *   betRateLimitPerSecond:    Redis token-bucket rate limit per playerId. Default 10.
 *
 * Lives under the tenant config service so it inherits the same two-admin
 * approval flow as the other surfaces.
 */
export const abuseControlsSchema = z
  .object({
    killSwitch: z.boolean().optional(),
    betCapPerRound: z.number().int().min(1).max(1000).optional(),
    betRateLimitPerSecond: z.number().int().min(1).max(1000).optional(),
  })
  .strict();
export type AbuseControlsOverride = z.infer<typeof abuseControlsSchema>;

/**
 * Maintenance mode — per-integrator-game pause. When `enabled`, the
 * auto-scheduler stops opening new rounds and the bet-accept path rejects
 * with GAME_UNDER_MAINTENANCE; the iframe shows `message` as a banner. The
 * GLOBAL maintenance flag (Redis, see @apx/shared) overrides this and
 * covers every game at once — used for platform-wide deploys/hotfixes.
 */
export const maintenanceSchema = z
  .object({
    enabled: z.boolean().optional(),
    message: z.string().max(200).optional(),
  })
  .strict();
export type MaintenanceOverride = z.infer<typeof maintenanceSchema>;

/**
 * Table limits + chip rail — the player-facing money bounds of a game.
 *
 *   minBet / maxBet:  per-bet credit bounds, enforced server-side by every
 *                     game module's validate() (config.limits). Stored as
 *                     numbers; the round-engine merge coerces to bigint.
 *   chips:            display denominations for the client chip rail/wheel.
 *                     Display-only — the server never trusts them for money.
 *
 * Cross-field guards: min ≤ max, chips ascending + unique, and when both
 * chips and bounds are present at least one chip must land inside
 * [minBet, maxBet] — otherwise the rail can't place a single legal bet.
 */
const tableFields = z.object({
  minBet: z.number().int().min(1).max(1_000_000_000).optional(),
  maxBet: z.number().int().min(1).max(1_000_000_000).optional(),
  /**
   * Tighter per-spot ceiling for high-multiplier SIDE bets (e.g. Dragon Tiger
   * Tie / suit / sum positions). Must sit within [minBet, maxBet]. Games
   * without side bets ignore it. Undefined → side bets use maxBet.
   */
  sideBetMaxBet: z.number().int().min(1).max(1_000_000_000).optional(),
  /**
   * Ceiling on what one player's bets in one round may return in total, stake
   * included. Only games whose player picks the multiplier enforce it (see
   * `MAX_PAYOUT_DEFAULTS_BY_CURRENCY`); others ignore it. A wider bound than
   * the stake fields: it is a payout, many stakes deep. Undefined → the
   * currency's default.
   */
  maxPayout: z.number().int().min(1).max(1_000_000_000_000).optional(),
  chips: z.number().int().min(1).max(1_000_000_000).array().min(1).max(8).optional(),
});

function refineTable(v: z.infer<typeof tableFields>, ctx: z.RefinementCtx): void {
  if (v.minBet !== undefined && v.maxBet !== undefined && v.minBet > v.maxBet) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'minBet must be ≤ maxBet',
      path: ['minBet'],
    });
  }
  if (v.sideBetMaxBet !== undefined) {
    const lo = v.minBet ?? 1;
    const hi = v.maxBet ?? Number.MAX_SAFE_INTEGER;
    if (v.sideBetMaxBet < lo) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'sideBetMaxBet must be ≥ minBet',
        path: ['sideBetMaxBet'],
      });
    }
    if (v.sideBetMaxBet > hi) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'sideBetMaxBet must be ≤ maxBet',
        path: ['sideBetMaxBet'],
      });
    }
  }
  if (v.maxPayout !== undefined && v.minBet !== undefined && v.maxPayout < v.minBet) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'maxPayout must be ≥ minBet — below it no bet can be placed',
      path: ['maxPayout'],
    });
  }
  if (v.chips) {
    for (let i = 1; i < v.chips.length; i++) {
      if (v.chips[i]! <= v.chips[i - 1]!) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'chips must be ascending and unique',
          path: ['chips'],
        });
        break;
      }
    }
    const lo = v.minBet ?? 1;
    const hi = v.maxBet ?? Number.MAX_SAFE_INTEGER;
    if (!v.chips.some((c) => c >= lo && c <= hi)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'at least one chip must fall inside [minBet, maxBet]',
        path: ['chips'],
      });
    }
  }
}

export const tableSchema = tableFields.strict().superRefine(refineTable);
export type TableOverride = z.infer<typeof tableSchema>;

/**
 * Each game's chip rail has a FIXED number of slots — admins retune the slot
 * VALUES, never the count. Sicbo + Dragon Tiger render disc + label, so any
 * credit values work, but always exactly 4 of them.
 */
export const CHIP_SLOTS: Record<string, number> = {
  greedyking: 5,
  sicbo: 4,
  dragontiger: 4,
  singaporelottery: 4,
  baccarat: 4,
  shortdicerush: 4,
  longdicerush: 4,
  wingo: 4,
  fishprawncrab: 4,
  crash: 4,
  mineschest: 4,
  spacehero: 4,
  animalparty: 4,
};

/**
 * Games whose stake MUST be a multiple of a given number of minor units (USD
 * cents, whole units elsewhere), because their payout multiplier is finer than
 * a whole number.
 *
 * `payout = floor(stake × returnBps / 10000)`. That division is exact for every
 * rate in a game's paytable only when the stake is a multiple of
 * `lcm(10000 / gcd(returnBps, 10000))` over that paytable. Below it the floor
 * silently discards part of every win — a 1-unit stake on a 1.99x target
 * returns 48%, not 96% — and nothing errors, so only a check like this finds it.
 *
 * Crash needs 100 because the player names the multiplier, to two decimals, so
 * every hundredth is reachable. The games with fixed paytables need less:
 * sicbo 1 (all whole multipliers), dragontiger 10 (1.80x sides, 0.5x tie
 * refund), baccarat 20 (Banker 1.95x) — see docs/games/crash/GAME_DESIGN_DOC.md
 * §8.1a. Crash, Wingo, Fish Prawn Crab and Animal Party are enforced from this
 * map, by their table schemas below; the others are recorded so the next
 * person can see the shape.
 *
 * Wingo needs 50: its shipped rates need 25, 10 and 5 credits to divide
 * exactly, and the grid is their least common multiple. See WINGO_STAKE_GRID.
 *
 * Fish Prawn Crab needs 100: its Three Soldiers returns (20 800, 31 600,
 * 42 400) and its Pair (129 600) each need 25, its Long Card (69 100) needs
 * 100, and the grid is their least common multiple. See
 * FISHPRAWNCRAB_STAKE_GRID.
 */
export const STAKE_GRANULARITY_BY_GAME: Record<string, number> = {
  crash: 100,
  wingo: WINGO_STAKE_GRID,
  fishprawncrab: FISHPRAWNCRAB_STAKE_GRID,
  // Space Hero: the player names the multiplier to two decimals, so every
  // hundredth is a reachable rate and only a multiple of 100 divides exactly at
  // all of them. Mirrors `STAKE_GRANULARITY` in @apx/game-spacehero; this
  // package does not depend on the game, so the pin lives in admin-api's
  // `spacehero-stake-grid.test.ts`, which imports both.
  spacehero: 100,
  // Derived, not typed. Every Animal Party rate is a multiple of the step, so
  // `gcd(10000, every bps)` is at least the step and the exact-payout grid can
  // never be coarser than `10000 / step` — for the shipped paytable and for any
  // paytable the multipliers schema will accept. The test pins it to the
  // package's own `STAKE_GRID_CREDITS`, which is derived from the paytable.
  animalparty: BPS_SCALE / ANP_RETURN_BPS_STEP,
};

/**
 * Baked table defaults per game — module `limits` + each client rail's chip
 * values. Single source for the admin-ui form defaults AND the write-time
 * cross-surface correlation check (a proposal may carry only one of
 * limits/chips; the counterpart comes from the stored override or these).
 */
export const TABLE_DEFAULTS: Record<string, { minBet: number; maxBet: number; chips: number[] }> = {
  greedyking: { minBet: 500, maxBet: 50_000, chips: [500, 1_000, 5_000, 10_000, 50_000] },
  sicbo: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
  dragontiger: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
  singaporelottery: { minBet: 100, maxBet: 10_000, chips: [100, 1_000, 5_000, 10_000] },
  // maxBet lifted to the schema's own ceiling (1e9) — effectively "no limit",
  // on request. This is a RISK CONTROL, not a layout number: it is the per-bet
  // ceiling the engine enforces on every stake. Put a real figure back before
  // this reaches a live tenant.
  baccarat: { minBet: 1_000, maxBet: 1_000_000_000, chips: [1_000, 5_000, 10_000, 50_000] },
  // Both Dice Rush games. Every value here is a multiple of 25 minor units — see
  // `refineDiceRushStakeGrid` below for why that is not a style choice.
  shortdicerush: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
  longdicerush: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
  // Wingo. Every value is on the game's stake grid — see
  // STAKE_GRANULARITY_BY_GAME and `refineWingoStakeGrid` below.
  wingo: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
  // Fish Prawn Crab. Every value is on the game's 100-credit stake grid — see
  // STAKE_GRANULARITY_BY_GAME and `refineFishPrawnCrabStakeGrid` below. The
  // same per-spot maximum for every family, the Pair included.
  fishprawncrab: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
  // Every value a multiple of 100 — see STAKE_GRANULARITY_BY_GAME.
  crash: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
  mineschest: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
  // Space Hero. Every value is a multiple of 100: see `refineSpaceHeroStakeGrid`.
  spacehero: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
  // Animal Party. Every value on its stake grid — see `refineAnimalPartyStakeGrid`.
  animalparty: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
};

/**
 * Baked per-player, per-round payout caps per (game, currency), in minor units
 * like every amount here (USD in cents). A game is listed only when its player
 * picks the multiplier: there a stake cap bounds nothing, because the same
 * stake pays up to 10,000x. Fixed-paytable games are bounded by their per-spot
 * `maxBet` and are not listed.
 *
 * Kept apart from `TABLE_DEFAULTS_BY_CURRENCY` so a currency can carry a cap
 * before it has a bet-limit row of its own (PHP). Being listed here is also
 * what puts the "Max payout" field on the admin table-limits form, and a guard
 * test holds every listed game to implementing `potentialPayout`.
 */
export const MAX_PAYOUT_DEFAULTS_BY_CURRENCY: Record<string, Record<CurrencyCode, number>> = {
  crash: {
    IDR: 500_000_000,
    USD: 2_700_000, // $27,000.00
    THB: 900_000,
    PHP: 1_700_000,
  },
};

/**
 * Base-bet → chip-ladder multipliers per game. In the native-currency model an
 * admin sets ONE base bet (== the currency's minBet) per (game, currency); the
 * chip rail derives as `base × CHIP_MULTIPLIERS[game]`, giving a
 * currency-agnostic ladder that scales with the unit (IDR base 500 and THB base
 * 10 reuse the same shape). Each array's length matches `CHIP_SLOTS[game]`; the
 * admin editor still lets an operator override individual slots as an escape
 * hatch. Verified against `TABLE_DEFAULTS_BY_CURRENCY`: greedyking IDR 500 →
 * [500,1K,5K,10K,50K]; sicbo/DT/baccarat IDR 1000 → [1K,5K,10K,50K].
 */
export const CHIP_MULTIPLIERS: Record<string, number[]> = {
  greedyking: [1, 2, 10, 20, 100],
  crash: [1, 5, 10, 50],
  mineschest: [1, 5, 10, 50],
  sicbo: [1, 5, 10, 50],
  dragontiger: [1, 5, 10, 50],
  singaporelottery: [1, 10, 50, 100],
  baccarat: [1, 5, 10, 50],
  shortdicerush: [1, 5, 10, 50],
  longdicerush: [1, 5, 10, 50],
  wingo: [1, 5, 10, 50],
  fishprawncrab: [1, 5, 10, 50],
  spacehero: [1, 5, 10, 50],
  animalparty: [1, 5, 10, 50],
};

/** Derive a game's chip ladder from a base bet: `base × CHIP_MULTIPLIERS[game]`,
 *  rounded to whole units. Falls back to the sicbo/DT shape for unknown games. */
export function deriveChips(gameId: string, baseBet: number): number[] {
  const mults = CHIP_MULTIPLIERS[gameId] ?? CHIP_MULTIPLIERS.sicbo!;
  return mults.map((m) => Math.round(baseBet * m));
}

const chipsExactly = (n: number) =>
  z.number().int().min(1).max(1_000_000_000).array().length(n).optional();

/** Editable-chip games: every slot value must be placeable — a chip below
 *  minBet or above maxBet is a dead slot the admin can simply retune away. */
function refineAllChipsInWindow(v: z.infer<typeof tableFields>, ctx: z.RefinementCtx): void {
  refineTable(v, ctx);
  if (!v.chips) return;
  const lo = v.minBet ?? 1;
  const hi = v.maxBet ?? Number.MAX_SAFE_INTEGER;
  const dead = v.chips.filter((c) => c < lo || c > hi);
  if (dead.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `every chip must sit inside [minBet, maxBet] — outside: ${dead.join(', ')}`,
      path: ['chips'],
    });
  }
}

export const sicboTableSchema = tableFields
  .extend({ chips: chipsExactly(CHIP_SLOTS.sicbo!) })
  .strict()
  .superRefine(refineAllChipsInWindow);
export const dragontigerTableSchema = tableFields
  .extend({ chips: chipsExactly(CHIP_SLOTS.dragontiger!) })
  .strict()
  .superRefine(refineAllChipsInWindow);
export const singaporeLotteryTableSchema = tableFields
  .extend({ chips: chipsExactly(CHIP_SLOTS.singaporelottery!) })
  .strict()
  .superRefine(refineAllChipsInWindow);
/** Crash additionally enforces the stake-granularity rule above: an off-grid
 *  chip would underpay every win on it, invisibly. */
function refineCrashStakeGrid(v: z.infer<typeof tableFields>, ctx: z.RefinementCtx): void {
  refineAllChipsInWindow(v, ctx);
  const g = STAKE_GRANULARITY_BY_GAME.crash!;
  const offGrid = [
    ...(v.chips ?? []).map((c, i) => ({ value: c, path: ['chips', i] as (string | number)[] })),
    ...(v.minBet !== undefined
      ? [{ value: v.minBet, path: ['minBet'] as (string | number)[] }]
      : []),
    ...(v.maxBet !== undefined
      ? [{ value: v.maxBet, path: ['maxBet'] as (string | number)[] }]
      : []),
  ].filter((e) => e.value % g !== 0);
  for (const e of offGrid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `crash stakes must be a multiple of ${g} in the currency's smallest unit (1.00 in USD) — ${e.value} would underpay every win placed on it`,
      path: e.path,
    });
  }
}

export const crashTableSchema = tableFields
  .extend({ chips: chipsExactly(CHIP_SLOTS.crash!) })
  .strict()
  .superRefine(refineCrashStakeGrid);
/** Mines Chest: four chips, each inside the table's limits. No stake grid: a
 *  win is rounded down to the smallest amount the currency is counted in, as
 *  on every table, and that is exact whenever 100 divides the stake. */
export const mineschestTableSchema = tableFields
  .extend({ chips: chipsExactly(CHIP_SLOTS.mineschest!) })
  .strict()
  .superRefine(refineAllChipsInWindow);
export const baccaratTableSchema = tableFields
  .extend({ chips: chipsExactly(CHIP_SLOTS.baccarat!) })
  .strict()
  .superRefine(refineAllChipsInWindow);

/**
 * Space Hero's stake grid: every chip and both table limits must be a multiple
 * of `STAKE_GRANULARITY_BY_GAME.spacehero` currency units.
 *
 * A win returns `floor(stake × targetBps / 10000)` and the target is any
 * two-decimal multiplier, so the division is exact for every target only when
 * 100 divides the stake. Off the grid the floor discards part of every win and
 * nothing errors: a 1-unit stake on a 1.99x target returns 1, not 1.99.
 *
 * Refused at CONFIG time, not at bet time. A player must never have a bet
 * rejected because an operator edited a ladder, and an operator must not be
 * able to save a ladder that underpays. `maxBet` is held to the grid as well as
 * `minBet`, so the largest stake the table accepts is itself exact.
 */
function refineSpaceHeroStakeGrid(v: z.infer<typeof tableFields>, ctx: z.RefinementCtx): void {
  refineAllChipsInWindow(v, ctx);
  const grid = STAKE_GRANULARITY_BY_GAME.spacehero!;
  const candidates: Array<{ value: number; path: (string | number)[] }> = [
    ...(v.chips ?? []).map((chip, i) => ({ value: chip, path: ['chips', i] })),
    ...(v.minBet !== undefined ? [{ value: v.minBet, path: ['minBet'] }] : []),
    ...(v.maxBet !== undefined ? [{ value: v.maxBet, path: ['maxBet'] }] : []),
  ];
  for (const { value, path } of candidates) {
    if (value % grid === 0) continue;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Space Hero stakes must be a multiple of ${grid}: ${value} would underpay every win placed on it`,
      path,
    });
  }
}

export const spaceheroTableSchema = tableFields
  .extend({ chips: chipsExactly(CHIP_SLOTS.spacehero!) })
  .strict()
  .superRefine(refineSpaceHeroStakeGrid);

/**
 * Smallest stake on which a Dice Rush payout is EXACT.
 *
 * Both games ship the same three rates (19200 / 22800 / 129600 bps), and all
 * three share `gcd(bps, 10000) = 400`, so `floor(stake × bps / 10000)` is exact
 * only when the stake is a multiple of `10000 / 400 = 25`. Off the grid every
 * win is quietly floored down: a 10-credit stake on a 1.92 area returns 19
 * rather than 19.2 — an effective 1.90 and a 95.0% RTP no player is ever told
 * about, and which no test on a 1000-credit stake would catch.
 *
 * So the grid is refused at CONFIG time rather than at bet time. A player must
 * never have a bet rejected because an operator edited a ladder; an operator
 * must not be able to save a ladder that underpays. Mirrors each game's own
 * `STAKE_GRID_CREDITS`, pinned by `rtp-solver.dicerush.test.ts`.
 */
const DICERUSH_STAKE_GRID = 25;

function refineDiceRushStakeGrid(v: z.infer<typeof tableFields>, ctx: z.RefinementCtx): void {
  refineAllChipsInWindow(v, ctx);
  const offGrid: number[] = [];
  for (const c of v.chips ?? []) if (c % DICERUSH_STAKE_GRID !== 0) offGrid.push(c);
  if (v.minBet != null && v.minBet % DICERUSH_STAKE_GRID !== 0) offGrid.push(v.minBet);
  if (offGrid.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        `Dice Rush stakes must be multiples of ${DICERUSH_STAKE_GRID} in the currency's smallest unit ` +
        `(0.25 in USD) or every win is silently rounded down: ${offGrid.join(', ')}`,
    });
  }
}

export const shortDiceRushTableSchema = tableFields
  .extend({ chips: chipsExactly(CHIP_SLOTS.shortdicerush!) })
  .strict()
  .superRefine(refineDiceRushStakeGrid);
export const longDiceRushTableSchema = tableFields
  .extend({ chips: chipsExactly(CHIP_SLOTS.longdicerush!) })
  .strict()
  .superRefine(refineDiceRushStakeGrid);

/**
 * Wingo's stake grid: every chip value and the minimum bet must be a multiple
 * of `STAKE_GRANULARITY_BY_GAME.wingo` credits.
 *
 * Settlement floors `stake × bps / 10000`. Off the grid every win is quietly
 * floored down: a 10-credit stake on a 1.96 spot returns 19 rather than 19.6,
 * an effective 1.90 no player is ever told about, and which no test on a
 * 1000-credit stake would catch.
 *
 * Refused at CONFIG time rather than at bet time, for the same reason as the
 * other grids in this file: a player must never have a bet rejected because an
 * operator edited a ladder, and an operator must not be able to save a ladder
 * that underpays. `schemas.wingo.test.ts` holds this check to the package's own
 * `isOnStakeGrid`, value by value.
 */
function refineWingoStakeGrid(v: z.infer<typeof tableFields>, ctx: z.RefinementCtx): void {
  refineAllChipsInWindow(v, ctx);
  const grid = STAKE_GRANULARITY_BY_GAME.wingo!;
  const offGrid: number[] = [];
  for (const c of v.chips ?? []) if (c % grid !== 0) offGrid.push(c);
  if (v.minBet != null && v.minBet % grid !== 0) offGrid.push(v.minBet);
  if (offGrid.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        `Wingo stakes must be multiples of ${grid} credits or every win is silently rounded ` +
        `down: ${offGrid.join(', ')}`,
    });
  }
}

export const wingoTableSchema = tableFields
  .extend({ chips: chipsExactly(CHIP_SLOTS.wingo!) })
  .strict()
  .superRefine(refineWingoStakeGrid);

/**
 * Fish Prawn Crab's stake grid: every chip value and the minimum bet must be a
 * multiple of `STAKE_GRANULARITY_BY_GAME.fishprawncrab` credits.
 *
 * Settlement floors `stake × bps / 10000`. Off the grid every win is quietly
 * floored down: a 50-credit stake on a 6.91 Long Card returns 345 rather than
 * 345.5, an effective 6.90 no player is ever told about, and which no test on a
 * 1000-credit stake would catch.
 *
 * Refused at CONFIG time rather than at bet time, for the same reason as the
 * other grids in this file: a player must never have a bet rejected because an
 * operator edited a ladder, and an operator must not be able to save a ladder
 * that underpays. `schemas.fishprawncrab.test.ts` holds this check to the
 * package's own `isOnStakeGrid`, value by value.
 */
function refineFishPrawnCrabStakeGrid(
  v: z.infer<typeof tableFields>,
  ctx: z.RefinementCtx,
): void {
  refineAllChipsInWindow(v, ctx);
  const grid = STAKE_GRANULARITY_BY_GAME.fishprawncrab!;
  const offGrid: number[] = [];
  for (const c of v.chips ?? []) if (c % grid !== 0) offGrid.push(c);
  if (v.minBet != null && v.minBet % grid !== 0) offGrid.push(v.minBet);
  if (offGrid.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        `Fish Prawn Crab stakes must be multiples of ${grid} credits or every win is silently ` +
        `rounded down: ${offGrid.join(', ')}`,
    });
  }
}

/**
 * Animal Party additionally holds every chip and the minimum bet to its stake
 * grid (`STAKE_GRANULARITY_BY_GAME.animalparty`). Off it, a win on the group
 * that pays a fractional multiple is floored down, on every bet, with nothing
 * to show for it but a lower return than the table advertises.
 *
 * Refused at CONFIG time, not at bet time: a player must never have a bet
 * rejected because an operator edited a ladder, and an operator must not be
 * able to save a ladder that underpays. `maxBet` is left alone on purpose: it
 * is a ceiling on a cumulative total, not a stake anyone places, and the
 * stakes that reach it are sums of on-grid chips.
 */
function refineAnimalPartyStakeGrid(v: z.infer<typeof tableFields>, ctx: z.RefinementCtx): void {
  refineAllChipsInWindow(v, ctx);
  const g = STAKE_GRANULARITY_BY_GAME.animalparty!;
  const offGrid = [
    ...(v.chips ?? []).map((c, i) => ({ value: c, path: ['chips', i] as (string | number)[] })),
    ...(v.minBet !== undefined
      ? [{ value: v.minBet, path: ['minBet'] as (string | number)[] }]
      : []),
  ].filter((e) => e.value % g !== 0);
  for (const e of offGrid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Animal Party stakes must be a multiple of ${g} credits — ${e.value} would round every win on it down`,
      path: e.path,
    });
  }
}

export const fishprawncrabTableSchema = tableFields
  .extend({ chips: chipsExactly(CHIP_SLOTS.fishprawncrab!) })
  .strict()
  .superRefine(refineFishPrawnCrabStakeGrid);

export const animalPartyTableSchema = tableFields
  .extend({ chips: chipsExactly(CHIP_SLOTS.animalparty!) })
  .strict()
  .superRefine(refineAnimalPartyStakeGrid);

/**
 * Legacy sprite denominations (pre-native-currency). Kept exported — the
 * client still imports it for the baked chip art — but no longer referenced
 * by the schema below.
 */
export const GREEDYKING_CHIP_SPRITES = [500, 1_000, 5_000, 10_000, 50_000] as const;

/**
 * GreedyKING chips are now admin-configurable like sicbo/dragontiger (art
 * gets swapped to match later); slot count stays fixed at CHIP_SLOTS.greedyking.
 */
export const greedykingTableSchema = tableFields
  .extend({ chips: chipsExactly(CHIP_SLOTS.greedyking!) })
  .strict()
  .superRefine(refineAllChipsInWindow);

/**
 * Cross-surface correlation check for the EFFECTIVE table config (defaults
 * overlaid by stored override overlaid by a proposal — composed by the
 * caller). Catches what per-payload zod cannot: a limits-only proposal that
 * strands the stored/default chips, or a chips-only proposal outside the
 * stored limits. Returns human-readable violations; empty = OK.
 */
export function tableCorrelationViolations(
  gameId: string,
  effective: { minBet?: number; maxBet?: number; chips?: number[] },
): string[] {
  const defaults = TABLE_DEFAULTS[gameId];
  const minBet = effective.minBet ?? defaults?.minBet ?? 1;
  const maxBet = effective.maxBet ?? defaults?.maxBet ?? Number.MAX_SAFE_INTEGER;
  const chips = effective.chips ?? defaults?.chips ?? [];
  const out: string[] = [];
  if (minBet > maxBet) out.push(`minBet ${minBet} exceeds maxBet ${maxBet}`);
  if (chips.length > 0) {
    const dead = chips.filter((c) => c < minBet || c > maxBet);
    if (dead.length > 0) {
      out.push(`chips outside limits [${minBet}, ${maxBet}]: ${dead.join(', ')}`);
    }
  }
  return out;
}

/**
 * Platform-supported currencies (native-currency model — no credit↔fiat
 * conversion; the currency IS the unit). A player has one fixed currency for
 * the life of their account; admins set table limits per (game, currency).
 *
 * This is a SEED list + fallback default set, not a closed enum — the
 * platform currency registry is admin-editable at runtime (new currencies
 * can be added without a schema change), mirroring how `IntegratorFiatCurrency`
 * is validated today (DB registry, checked at the admin-api layer). `CurrencyCode`
 * is therefore a plain `string`; this schema only enforces well-formed shape
 * (`z.string().regex(...)`), never registry membership.
 */
export const SUPPORTED_CURRENCIES = ['IDR', 'USD', 'THB'] as const;
export type CurrencyCode = string;

/** Well-formed currency code shape (ISO-4217-ish: 3–10 uppercase letters). Registry
 *  membership (is this currency actually enabled for the platform/integrator) is
 *  checked elsewhere, not here. */
const currencyCodePattern = /^[A-Z]{3,10}$/;

/**
 * Baked table defaults per (game, currency) — the starting point admins tune
 * from before any override exists. Every number is in the currency's MINOR
 * unit (#519): USD is counted in cents, so `100` is one dollar; IDR and THB
 * are counted in whole units. IDR values mirror `TABLE_DEFAULTS`; USD/THB are
 * admin-tunable starting points, not FX-derived.
 */
export const TABLE_DEFAULTS_BY_CURRENCY: Record<
  string,
  Partial<Record<CurrencyCode, { minBet: number; maxBet: number; chips: number[] }>>
> = {
  greedyking: {
    IDR: { minBet: 500, maxBet: 50_000, chips: [500, 1_000, 5_000, 10_000, 50_000] },
    THB: { minBet: 10, maxBet: 1_000, chips: [10, 20, 100, 200, 1_000] },
    USD: { minBet: 100, maxBet: 100_000, chips: [100, 500, 1_000, 5_000, 10_000] },
  },
  // Crash: every chip and both limits are multiples of 100 minor units in
  // EVERY currency. THB starts higher than the other games' ladders do: THB is
  // counted in whole baht, so a 20 baht chip would be off the grid and would
  // underpay every win placed on it. USD is counted in cents, so its grid is
  // one dollar and the ladder starts at $1. See STAKE_GRANULARITY_BY_GAME.
  crash: {
    IDR: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
    THB: { minBet: 100, maxBet: 20_000, chips: [100, 500, 1_000, 5_000] },
    USD: { minBet: 100, maxBet: 2_000_000, chips: [100, 500, 1_000, 5_000] },
  },
  // Mines Chest: the rows the tables with this chip shape use. Its lowest
  // payout is 1.92x and a win is rounded down to whole units, so the IDR row
  // and the THB chips of 100 and up are exact while a THB 20 chip on one chest
  // pays 38 for 38.40. The USD row is written for USD counted in cents: on
  // whole dollars a 1 chip on one chest would pay 1, so this game's USD table
  // should stay off until the platform counts USD in cents.
  mineschest: {
    IDR: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
    THB: { minBet: 20, maxBet: 4_000, chips: [20, 100, 200, 1_000] },
    USD: { minBet: 1, maxBet: 2_000, chips: [1, 5, 10, 50] },
  },
  sicbo: {
    IDR: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
    THB: { minBet: 20, maxBet: 4_000, chips: [20, 100, 200, 1_000] },
    USD: { minBet: 100, maxBet: 200_000, chips: [100, 500, 1_000, 5_000] },
  },
  dragontiger: {
    IDR: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
    THB: { minBet: 20, maxBet: 4_000, chips: [20, 100, 200, 1_000] },
    USD: { minBet: 100, maxBet: 200_000, chips: [100, 500, 1_000, 5_000] },
  },
  singaporelottery: {
    IDR: { minBet: 100, maxBet: 10_000, chips: [100, 1_000, 5_000, 10_000] },
    THB: { minBet: 10, maxBet: 1_000, chips: [10, 100, 500, 1_000] },
    USD: { minBet: 100, maxBet: 10_000, chips: [100, 1_000, 5_000, 10_000] },
  },
  baccarat: {
    // IDR maxBet lifted to the schema ceiling — see the note on TABLE_DEFAULTS.
    // THB and USD keep their own ceilings: nobody asked for those, and they are
    // real limits on their own scales.
    IDR: { minBet: 1_000, maxBet: 1_000_000_000, chips: [1_000, 5_000, 10_000, 50_000] },
    THB: { minBet: 10, maxBet: 1_000, chips: [10, 50, 100, 500] },
    USD: { minBet: 100, maxBet: 10_000, chips: [100, 500, 1_000, 5_000] },
  },
  // Both Dice Rush games: every value a multiple of 25 minor units (see
  // `refineDiceRushStakeGrid`). Before these rows every currency fell back to
  // `TABLE_DEFAULTS`, which in USD cents is a $10 minimum. IDR and THB keep
  // exactly that fallback; a THB ladder of its own on the 25 grid is still to
  // be chosen.
  shortdicerush: {
    IDR: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
    THB: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
    USD: { minBet: 100, maxBet: 200_000, chips: [100, 500, 1_000, 5_000] },
  },
  longdicerush: {
    IDR: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
    THB: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
    USD: { minBet: 100, maxBet: 200_000, chips: [100, 500, 1_000, 5_000] },
  },
  // Wingo: every chip, minimum and maximum is on the game's 50-minor-unit stake
  // grid in EVERY currency. THB has its own row because THB is counted in whole
  // baht and the ladder most tables use, built on a base of 20, is off that grid
  // and would underpay every win placed on it; 50 is the smallest minimum the
  // grid allows. USD is counted in cents, so the grid is $0.50 and the usual $1
  // ladder is already on it. See STAKE_GRANULARITY_BY_GAME.
  wingo: {
    IDR: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
    THB: { minBet: 50, maxBet: 10_000, chips: [50, 250, 500, 2_500] },
    USD: { minBet: 100, maxBet: 200_000, chips: [100, 500, 1_000, 5_000] },
  },
  // Fish Prawn Crab: every chip, minimum and maximum is on the game's
  // 100-minor-unit stake grid in EVERY currency. THB has its own row because
  // the ladder most tables use, built on a base of 20, is off that grid and
  // would underpay every win placed on it. USD is counted in cents, so its row
  // is the Dice Rush one: $1 · $5 · $10 · $50 chips, $1 minimum, $2,000
  // maximum. 100 is the smallest minimum the grid allows. See
  // STAKE_GRANULARITY_BY_GAME.
  fishprawncrab: {
    IDR: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
    THB: { minBet: 100, maxBet: 20_000, chips: [100, 500, 1_000, 5_000] },
    USD: { minBet: 100, maxBet: 200_000, chips: [100, 500, 1_000, 5_000] },
  },
  // Space Hero: every chip and both limits are multiples of 100 in EVERY
  // currency. The platform is native-currency (the currency IS the unit, with
  // no sub-unit), so a base bet of 1 or 20 would sit off the grid and underpay
  // every win placed on it. THB and USD therefore start at 100.
  spacehero: {
    IDR: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
    THB: { minBet: 100, maxBet: 20_000, chips: [100, 500, 1_000, 5_000] },
    USD: { minBet: 100, maxBet: 20_000, chips: [100, 500, 1_000, 5_000] },
  },
  // Animal Party: every chip and every minimum is on its stake grid in EVERY
  // currency. IDR and THB are the usual ladders, which already sit on it. USD
  // is not the `1, 5, 10, 50` the other tables use: the platform is
  // native-currency (no sub-unit), so a 1 or 5 chip would be off the grid and
  // would round every fractional-multiple win down. It starts at 10 instead.
  animalparty: {
    IDR: { minBet: 1_000, maxBet: 200_000, chips: [1_000, 5_000, 10_000, 50_000] },
    THB: { minBet: 20, maxBet: 4_000, chips: [20, 100, 200, 1_000] },
    USD: { minBet: 10, maxBet: 2_000, chips: [10, 50, 100, 500] },
  },
};

/**
 * Per-currency table overrides — partial map keyed by currency code; each
 * entry validated by the generic (game-agnostic) `tableSchema`, same
 * cross-field guards as the legacy flat `table` surface. The legacy `table`
 * surface is unchanged and stays the IDR/default entry, so existing IDR
 * integrators keep working with zero migration.
 *
 * Key is an OPEN string (format-checked only, not `z.enum(SUPPORTED_CURRENCIES)`)
 * — the currency registry is admin-editable at runtime, so this schema can't
 * hard-code the allowed set. Registry membership is enforced elsewhere.
 */
export const tableByCurrencySchema = z.record(z.string().regex(currencyCodePattern), tableSchema);
export type TableByCurrencyOverride = z.infer<typeof tableByCurrencySchema>;

/** Per-game `table` schema — each fixes its own chip slot count, and the games
 *  with a fractional multiple add their stake grid. Games not listed fall back
 *  to the generic `tableSchema`. This is the ONE registry: admin-api validates
 *  writes with it and the admin-ui's Table limits form validates against the
 *  same map, so a game added here needs no entry anywhere else. */
export const TABLE_SCHEMA_BY_GAME: Record<string, z.ZodType> = {
  greedyking: greedykingTableSchema,
  sicbo: sicboTableSchema,
  dragontiger: dragontigerTableSchema,
  singaporelottery: singaporeLotteryTableSchema,
  baccarat: baccaratTableSchema,
  crash: crashTableSchema,
  mineschest: mineschestTableSchema,
  shortdicerush: shortDiceRushTableSchema,
  longdicerush: longDiceRushTableSchema,
  wingo: wingoTableSchema,
  fishprawncrab: fishprawncrabTableSchema,
  spacehero: spaceheroTableSchema,
  animalparty: animalPartyTableSchema,
};

/**
 * WRITE-time `tableByCurrency` schema for one game: every currency entry is held
 * to that game's own `table` rules. `tableByCurrencySchema` above is generic, and
 * it is the map the round engine enforces at bet time (`resolveCurrencyTable`) —
 * so validating writes with it let a crash USD rail of `[1, 5, 10, 50]` through,
 * underpaying every win on it, and let any game save more chips than it has
 * slots.
 *
 * Deliberately NOT used by `tenantGameConfigSchema`: that parses on READ, and a
 * stricter read would make one legacy entry wipe a tenant's whole override.
 */
export function tableByCurrencySchemaFor(gameId: string): z.ZodType {
  return z.record(
    z.string().regex(currencyCodePattern),
    TABLE_SCHEMA_BY_GAME[gameId] ?? tableSchema,
  );
}

/**
 * Provider-set numeric bounds a tenant's own config must stay inside.
 *
 * Shape: { [surface]: { [fieldName]: { min?, max? } } }. Keys are matched on
 * the LEAF field name at any depth, so one entry for `maxBet` bounds both
 * `table.maxBet` and every currency under `tableByCurrency` without
 * enumerating currencies.
 *
 * This is what makes self-service safe without an approval queue: the provider
 * sets the envelope, the tenant moves freely inside it, and anything outside
 * is refused rather than reviewed.
 */
export const envelopeBoundSchema = z
  .object({ min: z.number().optional(), max: z.number().optional() })
  .strict()
  .refine((b) => b.min === undefined || b.max === undefined || b.min <= b.max, {
    message: 'min must be ≤ max',
  });

export const envelopeSchema = z.record(z.record(envelopeBoundSchema));
export type ConfigEnvelope = z.infer<typeof envelopeSchema>;

export const tenantGameConfigSchema = z
  .object({
    timing: timingSchema.optional(),
    weights: weightsSchema.optional(),
    jackpot: jackpotSchema.optional(),
    bigWinner: bigWinnerSchema.optional(),
    leaderboard: leaderboardSchema.optional(),
    rules: rulesSchema.optional(),
    scheduler: schedulerSchema.optional(),
    abuse: abuseControlsSchema.optional(),
    maintenance: maintenanceSchema.optional(),
    multipliers: multipliersSchema.optional(),
    categoryBonus: categoryBonusSchema.optional(),
    streak: streakSchema.optional(),
    table: tableSchema.optional(),
    tableByCurrency: tableByCurrencySchema.optional(),
    /// Only meaningful on the reserved provider-default integrator, whose
    /// envelope bounds every real tenant's writes.
    envelope: envelopeSchema.optional(),
  })
  .strict();
export type IntegratorGameConfig = z.infer<typeof tenantGameConfigSchema>;

export const ALL_SURFACES = [
  'timing',
  'weights',
  'jackpot',
  'bigWinner',
  'leaderboard',
  'rules',
  'scheduler',
  'abuse',
  'maintenance',
  'multipliers',
  'categoryBonus',
  'streak',
  'table',
  'tableByCurrency',
  // Provider-only: the bounds every OTHER tenant-writable surface must respect.
  'envelope',
] as const;
export type ConfigSurface = (typeof ALL_SURFACES)[number];

/**
 * Surfaces only some games read. Every other surface applies to every game.
 *
 * These carry game-specific mechanics — greedyking's veg/meat pools and bonuses,
 * sicbo's triple-drain pool. Written onto any other game they validate (the
 * jackpot union and the category/streak shapes are game-blind), store, and do
 * nothing, while the admin who tuned them believes they changed the game.
 * Mirrors the admin-ui `TABS_BY_GAME` whitelist.
 */
export const GAME_SCOPED_SURFACES: Partial<Record<ConfigSurface, readonly string[]>> = {
  jackpot: ['greedyking', 'sicbo'],
  categoryBonus: ['greedyking'],
  streak: ['greedyking'],
  // Weights are worse than inert on the games that do not read them: the
  // engine's merge replaces `config.weights` wholesale for any game, and a
  // sicbo-shaped `{ die }` table is exactly what the fixed-dice tables draw
  // from. Scoped to the games with a weights shape, derived from that map.
  weights: Object.keys(WEIGHTS_SCHEMA_BY_GAME),
};

export function surfaceAppliesToGame(surface: ConfigSurface, gameId: string): boolean {
  const games = GAME_SCOPED_SURFACES[surface];
  return games === undefined || games.includes(gameId);
}

/**
 * A stored override with every surface this game does not read removed.
 *
 * Writes are already refused for those surfaces, but rows written before a
 * surface was scoped may still carry one, and the engine's merge would apply
 * it. Every read goes through `IntegratorConfigService.get`, which calls this,
 * so such a row is inert from the moment this ships, and the next write to the
 * row drops the stray surface for good.
 */
export function scopeToGame(gameId: string, config: IntegratorGameConfig): IntegratorGameConfig {
  let out: IntegratorGameConfig | undefined;
  for (const surface of Object.keys(GAME_SCOPED_SURFACES) as ConfigSurface[]) {
    if (config[surface] !== undefined && !surfaceAppliesToGame(surface, gameId)) {
      out ??= { ...config };
      delete out[surface];
    }
  }
  return out ?? config;
}

export const surfaceSchemas: Record<ConfigSurface, z.ZodType> = {
  timing: timingSchema,
  weights: weightsSchema,
  jackpot: jackpotSchema,
  bigWinner: bigWinnerSchema,
  leaderboard: leaderboardSchema,
  rules: rulesSchema,
  scheduler: schedulerSchema,
  abuse: abuseControlsSchema,
  maintenance: maintenanceSchema,
  multipliers: multipliersSchema,
  categoryBonus: categoryBonusSchema,
  streak: streakSchema,
  table: tableSchema,
  tableByCurrency: tableByCurrencySchema,
  envelope: envelopeSchema,
};

/**
 * Resolve the effective table limits for a (game, currency) pair. Priority,
 * highest first, merged PER FIELD so a partial override composes over the
 * default instead of replacing it wholesale:
 *   1. `cfg.tableByCurrency[currency]` — explicit per-currency override.
 *   2. legacy flat `cfg.table`, IDR only — back-compat for pre-multi-currency
 *      integrator overrides that never set `tableByCurrency`.
 *   3. `TABLE_DEFAULTS_BY_CURRENCY[gameId][currency]` — baked per-currency default.
 *   4. `TABLE_DEFAULTS[gameId]` — final fallback (legacy game default).
 * Always returns concrete numbers for the bet limits and chips — never
 * undefined. `maxPayout` follows the same order with
 * `MAX_PAYOUT_DEFAULTS_BY_CURRENCY` as its default, and is undefined when no
 * layer sets one: the game module's own default then applies.
 */
export function resolveCurrencyTable(
  cfg: IntegratorGameConfig | undefined,
  gameId: string,
  currency: CurrencyCode,
): { minBet: number; maxBet: number; chips: number[]; maxPayout?: number } {
  const perCurrency = cfg?.tableByCurrency?.[currency];
  const legacyIdr = currency === 'IDR' ? cfg?.table : undefined;
  const currencyDefault = TABLE_DEFAULTS_BY_CURRENCY[gameId]?.[currency];
  const gameDefault = TABLE_DEFAULTS[gameId];
  return {
    minBet:
      perCurrency?.minBet ??
      legacyIdr?.minBet ??
      currencyDefault?.minBet ??
      gameDefault?.minBet ??
      1,
    maxBet:
      perCurrency?.maxBet ??
      legacyIdr?.maxBet ??
      currencyDefault?.maxBet ??
      gameDefault?.maxBet ??
      Number.MAX_SAFE_INTEGER,
    chips:
      perCurrency?.chips ?? legacyIdr?.chips ?? currencyDefault?.chips ?? gameDefault?.chips ?? [],
    maxPayout:
      perCurrency?.maxPayout ??
      legacyIdr?.maxPayout ??
      MAX_PAYOUT_DEFAULTS_BY_CURRENCY[gameId]?.[currency],
  };
}
